import { useState } from "react";
import { Building2, Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatSite } from "@/lib/sites";
import type { ReferenceSite } from "@/lib/tickets";
import { cn } from "@/lib/utils";

/**
 * Picks one of twenty-nine sites, by typing.
 *
 * ⚠️ Searchable rather than a plain list because twenty-nine is past the number anybody
 * scans. A dropdown that long is chosen from by scrolling until something looks close,
 * which is how a request gets filed against the wrong facility — and the site is not
 * cosmetic once it is wrong: it is what the request is routed and reported against for the
 * rest of its life.
 *
 * ⚠️ RENDERING ONLY, like the picker it replaces. The server derives a ticket's site from
 * the requester's own record and falls back to the posted value only when they have none
 * (D-43), so nothing here is a control — a client that posts any site id it likes gets the
 * same answer, and one that posts none is refused (D-100).
 */
export function SiteCombobox({
  sites,
  value,
  onChange,
  disabled,
  required,
  invalid,
  id,
}: {
  sites: ReferenceSite[];
  /** The chosen site id, or `""` for no site in particular. */
  value: string;
  onChange: (siteId: string) => void;
  disabled?: boolean;

  /**
   * Whether "Not site-specific" is an answer here.
   *
   * ⚠️ It is not, on a request (D-100), and the option is REMOVED rather than left in place
   * to be refused later. A list offering an answer the form will not take is how somebody
   * arrives at a validation message about a choice the screen itself invited. The prompt
   * changes with it: "Not site-specific" describes a state, "Choose a site" asks a question.
   */
  required?: boolean;

  /** Whether to draw the trigger as unanswered. The message itself belongs to the caller. */
  invalid?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);

  const chosen = sites.find((s) => s.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          className={cn(
            "mt-1 h-10 w-full justify-between px-3 font-normal",
            invalid && "border-destructive",
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className={cn("truncate", !chosen && "text-muted-foreground")}>
              {chosen ? formatSite(chosen) : required ? "Choose a site" : "Not site-specific"}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        {/*
          ⚠️ Filtered in the browser over a list it already holds. The sites are reference
          data — twenty-nine rows, fetched once for the form — so there is no lookup
          endpoint here and no request per keystroke. Nothing about a site is anybody's
          personal data, which is what makes that safe; it is not the shape a PEOPLE picker
          may take.
        */}
        <Command>
          <CommandInput placeholder="Search by code or name…" />
          <CommandList>
            <CommandEmpty>No site matches that.</CommandEmpty>
            <CommandGroup>
              {/*
                Kept as an option rather than a clear button, WHERE IT IS AN ANSWER AT ALL.
                "Not site-specific" is a real answer to a filter — a password reset happens
                nowhere in particular — and burying it in a secondary control would make the
                list feel like it demands a choice.

                ⚠️ On a request it IS demanded (D-100), so `required` takes the option away
                rather than leaving somebody to be refused for choosing it.
              */}
              {!required && (
                <CommandItem
                  value="not site-specific none"
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value ? "opacity-0" : "opacity-100")} />
                  <span className="text-muted-foreground">Not site-specific</span>
                </CommandItem>
              )}

              {sites.map((s) => (
                /*
                  ⚠️ The searchable text is the code, the name AND the city, so "PLR",
                  "Palmer" and "Denver" all reach the same row. Somebody filing for a caller
                  on the phone has whichever of the three the caller said, and a picker that
                  only matched the name would fail on the one people actually use — the code
                  is what staff say to each other (see formatSite).

                  onSelect ignores its argument and closes over the site instead: cmdk
                  lower-cases the value it hands back, so matching on it would be a
                  case-collapse away from resolving to the wrong row.
                */
                <CommandItem
                  key={s.id}
                  value={`${s.code} ${s.name} ${s.city}`}
                  onSelect={() => {
                    onChange(s.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", value === s.id ? "opacity-100" : "opacity-0")}
                  />
                  <span className="truncate">{formatSite(s)}</span>
                  <span className="ml-auto pl-2 text-xs text-muted-foreground">{s.city}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
